




























































import { ClientAccountsApi } from '@/api/client-accounts';
import { CreateClientRequest, createClientRequest } from '@/api/requests/clients/create-client-request';
import addressWithLookup from '@/components/address-with-lookup.vue';
import addClientAccountDetails from '@/components/client/add-client/add-client-account-details.vue';
import addClientAdditionalDetails from '@/components/client/add-client/add-client-additional-details.vue';
import addClientDetails from '@/components/client/add-client/add-client-details.vue';
import contactPreferenceDetails from '@/components/client/add-client/contact-preference-details.vue';
import potentialDuplicateDetails from '@/components/client/add-client/potential-duplicate-details.vue';
import loading from '@/components/loading.vue';
import { ClientAccountDuplicate } from '@/shared/clients/client-account-duplicate';
import { PartnerPracticeType } from '@/shared/types/partner-practice-type';
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import { namespace } from 'vuex-class';

const typesModule = namespace('types');
const userModule = namespace('user');

const clientApi = new ClientAccountsApi();

@Component({
    components: {
        addressWithLookup,
        addClientAccountDetails,
        addClientAdditionalDetails,
        addClientDetails,
        contactPreferenceDetails,
        potentialDuplicateDetails,
        loading,
    },
})
export default class AddClientDialog extends Vue {
    @typesModule.Getter('activePartnerPracticeTypesForCurrentUser') private partnerPracticeTypes!: Array<PartnerPracticeType>;

    @userModule.Getter('isDataCreationExecutive') private isDataCreationExecutive!: boolean;
    @userModule.Getter('isDataCreationTeamLeader') private isDataCreationTeamLeader!: boolean;

    @Prop({ required: true }) private visible!: boolean;

    private isVisible: boolean = this.visible;
    private client: CreateClientRequest = createClientRequest();
    private currentStep: number = 0;
    private isBusy: boolean = false;
    private isCheckingDuplicates: boolean = false;
    private accountDetailsCompleted: boolean = false;
    private clientDetailsCompleted: boolean = false;
    private addressDetailsCompleted: boolean = false;
    private duplicateAccountsCompleted: boolean = false;
    private additionalInformationCompleted: boolean = false;
    private duplicateAccounts: Array<ClientAccountDuplicate> = new Array<ClientAccountDuplicate>();

    private get canSaveAddAnother(): boolean {
        return this.isDataCreationExecutive || this.isDataCreationTeamLeader;
    }

    private get maxSteps(): number {
        return this.duplicateAccounts.length ? 5 : 4;
    }

    @Watch('visible')
    private onVisibleChanged(visible: boolean): void {
        this.isVisible = visible;
        this.reset();
    }

    private async onNext(): Promise<void> {
        let currentStep = this.currentStep;

        if (this.currentStep > 2 && !this.duplicateAccounts.length) {
            currentStep++;
        }

        switch (currentStep) {
            case 0: // Account Details
                if (!(await (this.$refs.accountDetails as Vue).$validator.validateAll('account-details'))) {
                    return;
                }

                this.accountDetailsCompleted = true;
                break;

            case 1: // Client Details
                if (!(await (this.$refs.clientDetails as Vue).$validator.validateAll('client-details'))) {
                    return;
                }

                this.clientDetailsCompleted = true;
                break;

            case 2: // Address Details
                if (!(await (this.$refs.addressDetails as Vue).$validator.validateAll('address-details'))) {
                    return;
                }

                this.addressDetailsCompleted = true;

                if ((await this.getDuplicateClientAccounts()) < 1) {
                    this.duplicateAccountsCompleted = true;
                }

                break;

            case 3: // Potential Duplicates
                this.duplicateAccountsCompleted = true;
                break;

            case 4: // Additional Information
                if (!(await (this.$refs.additionalDetails as Vue).$validator.validateAll('national-insurance-number'))) {
                    return;
                }

                this.additionalInformationCompleted = true;
                break;
        }

        setTimeout(() => {
            this.currentStep++;
        }, 0);
    }

    private onPrevious(): void {
        this.currentStep--;
    }

    private async onSave(addAnother: boolean): Promise<void> {
        if (!(await this.$validator.validateAll('account-details')) && !(await this.$validator.validateAll('client-details')) &&
            !(await this.$validator.validateAll('address-details')) && !(await this.$validator.validateAll('national-insurance-number'))) {
            return;
        }

        this.isBusy = true;

        try {
            const id = await clientApi.createClientAccountAsync(this.client);

            if (addAnother) {
                if (this.client.id == null) {
                    this.$snackbar.open({ message: 'Client account successfully created.', type: 'is-success' });
                } else {
                    this.$snackbar.open({ message: 'Client account successfully merged and updated.', type: 'is-success' });
                }

                this.reset();
            } else {
                this.$emit('ok', { id, isNew: this.client.id == null });
            }
        } catch (e) {
            if (e instanceof Error) {
                this.$snackbar.open({ message: e.message, type: 'is-danger' });
            } else {
                this.$snackbar.open({ message: 'Could not create new client account, please try again.', type: 'is-danger' });
            }
        }

        this.isBusy = false;
    }

    private onCancel(): void {
        this.$emit('cancel');
    }

    private reset(): void {
        this.currentStep = 0;
        this.isBusy = false;
        this.accountDetailsCompleted = false;
        this.clientDetailsCompleted = false;
        this.addressDetailsCompleted = false;
        this.duplicateAccountsCompleted = false;
        this.additionalInformationCompleted = false;
        this.client = createClientRequest();
        this.duplicateAccounts = [];
        this.$validator.errors.clear();

        if (this.partnerPracticeTypes.length === 1) {
            this.client.partnerPracticeId = this.partnerPracticeTypes[0].id!;
        }
    }

    private async getDuplicateClientAccounts(): Promise<number> {
        this.isCheckingDuplicates = true;

        this.duplicateAccounts = [];

        const result = await clientApi.getDuplicatesAsync({ ...this.client });

        if (result != null && result.length) {
            this.duplicateAccounts = result.sort((n1, n2) => {
                if (n1.matchPercent! < n2.matchPercent!) { return 1; }
                if (n1.matchPercent! > n2.matchPercent!) { return -1; }
                return 0;
            });
        }

        this.isCheckingDuplicates = false;

        return this.duplicateAccounts.length;
    }

    private async mounted(): Promise<void> {
        await Promise.all([
            this.$store.dispatch(`partnerPractices/loadPartnerPracticesAsync`),
            this.$store.dispatch(`types/loadTypesAsync`),
            this.$store.dispatch(`types/loadPartnerPracticeTypesAsync`),
            this.$store.dispatch(`users/loadAllUsersAsync`),
        ]);

        if (this.partnerPracticeTypes.length === 1) {
            this.client.partnerPracticeId = this.partnerPracticeTypes[0].id!;
        }
    }
}
